import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import { articles } from './articles'
import { Button } from '../components/ui/button'
import { Logo } from '../components/Logo'
import { Clock, Calendar, ArrowRight } from 'lucide-react'

export function ArticlesIndex() {
  useEffect(() => {
    // Update page title and meta
    document.title = 'Sports Betting Articles & Guides | PitchMetric'
    
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert sports betting articles covering NFL, NBA, MLB, NCAA, DraftKings, FanDuel, Kalshi, and prediction markets. AI-powered betting strategies and analysis.')
    }

    // Track page view
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Articles Index',
        page_location: window.location.href,
        page_path: window.location.pathname
      })
    }

    // Scroll to top
    window.scrollTo(0, 0)
  }, [])

  const handleCTAClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'engagement',
        event_label: 'articles_index_cta'
      })
    }
    window.location.href = '/?cta=true'
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-black/90 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex h-16 sm:h-20 items-center justify-between">
            <Link to="/">
              <Logo />
            </Link>
            <Button 
              size="sm" 
              className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-4 sm:px-6 text-xs sm:text-sm h-9 sm:h-10"
              onClick={handleCTAClick}
            >
              Get Access
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block border border-white/10 px-3 py-1 text-xs tracking-widest text-gray-500 uppercase mb-6">
              Sports Betting Intelligence
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Expert Sports Betting Articles & Guides
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 leading-relaxed max-w-3xl">
              Comprehensive guides covering NFL, NBA, MLB, NCAA betting strategies, DraftKings and FanDuel tips, prediction markets, and AI-powered sports analysis.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {articles.map((article) => (
              <Link 
                key={article.id}
                to={`/articles/${article.slug}`}
                className="group"
              >
                <article className="h-full border border-white/10 rounded-2xl bg-black/50 p-6 sm:p-8 hover:border-[#00E5FF] transition-all duration-300 hover:shadow-lg hover:shadow-[#00E5FF]/10">
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1.5" />
                      {new Date(article.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-3 h-3 mr-1.5" />
                      {article.readTime}
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="font-heading text-xl sm:text-2xl font-bold mb-3 group-hover:text-[#00E5FF] transition-colors line-clamp-2">
                    {article.title}
                  </h2>

                  {/* Description */}
                  <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4 line-clamp-3">
                    {article.description}
                  </p>

                  {/* Read More Link */}
                  <div className="flex items-center text-[#00E5FF] text-sm font-medium group-hover:gap-2 transition-all">
                    Read Article
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 border-t border-white/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#00E5FF]/10 to-purple-500/10 border border-white/10 rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Ready to gain your edge?
              </h2>
              <p className="text-gray-300 text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
                Stop reading about sports betting and start winning. Get AI-powered intelligence for every game with PitchMetric.
              </p>
              <Button 
                size="lg"
                className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-8 h-12 text-base"
                onClick={handleCTAClick}
              >
                Get Early Access
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 sm:py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 mb-12">
              <div>
                <Link to="/">
                  <Logo className="mb-4" />
                </Link>
                <p className="text-xs sm:text-sm text-gray-600">
                  AI-powered sports intelligence platform
                </p>
              </div>
              
              <div>
                <h4 className="font-heading text-xs sm:text-sm font-medium mb-3 uppercase tracking-wider text-gray-500">Resources</h4>
                <ul className="space-y-2 text-xs sm:text-sm text-gray-400">
                  <li>
                    <Link to="/articles" className="hover:text-white transition-colors">
                      Articles
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="hover:text-white transition-colors">
                      Home
                    </Link>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-heading text-xs sm:text-sm font-medium mb-3 uppercase tracking-wider text-gray-500">Legal</h4>
                <ul className="space-y-2 text-xs sm:text-sm text-gray-400">
                  <li><a href="/terms.html" target="_blank" className="hover:text-white transition-colors">Terms</a></li>
                  <li><a href="/privacy.html" target="_blank" className="hover:text-white transition-colors">Privacy</a></li>
                </ul>
              </div>
            </div>
            
            <div className="pt-6 border-t border-white/5">
              <p className="text-xs sm:text-sm text-gray-600">
                © 2024 PitchMetric. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

